import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@material-ui/core/Box';

import { useTranslation } from 'hooks/useTranslation';
import { ContentLoader } from 'shared/loaders';
import { CreateOrderInput } from 'features/dashboard/ts/CreateOrderInput';
import { DetailsWithTitle } from 'features/dashboard/components/DetailsWithTitle';
import BaseButton from 'features/app/components/BaseButton';
import { SingleGiftCard } from 'features/dashboard/components/SingleGiftCard';
import { GiftOrderSummary } from 'features/dashboard/components/GiftOrderSummary';
import { useSingleOrderSummaryQuery } from 'features/dashboard/api/useSingleOrderSummaryQuery';

import { useStyles } from './styles';
import { AxiosError } from 'axios';
import { useMessage } from '../../../message/hooks/useMessage';
import { createOrderFailed } from '../../redux/actions';
import { useDispatch } from 'react-redux';
import { OrderDetailsItem } from '../OrderDetailsItem';

export interface ReviewDetailsStepProps {
  onSubmit: () => void;
}

export const ReviewDetailsStep = ({ onSubmit }: ReviewDetailsStepProps) => {
  const classes = useStyles();
  const { t } = useTranslation('dashboard');
  const { showErrorMessage } = useMessage();
  const dispatch = useDispatch();

  const { getValues, handleSubmit } = useFormContext<CreateOrderInput>();
  const {
    last_name,
    first_name,
    email,
    phone_number,
    product,
    amount,
    currency,
    company,
    designation,
    denomination,
    quantity,
    country,
  } = useMemo(() => getValues() as CreateOrderInput, [getValues]);

  const {
    data: orderSummary,
    isLoading,
    error,
  } = useSingleOrderSummaryQuery(
    {
      branded_giftcard: product?.id || '',
      currency: currency?.currency_id || '',
      first_name,
      last_name,
      email,
      company,
      designation,
      phone_number,
      denomination: denomination || null,
      gift_card_amount: amount,
      quantity,
      country: country?.code,
    },
    [product?.id, currency?.currency_id, email, designation, denomination, amount, quantity, country?.code]
  );

  useEffect(() => {
    if (isLoading || (error && orderSummary) || !error) return;
    const errorData = (error as AxiosError)?.response?.data;

    showErrorMessage(errorData?.email_validation?.message || errorData?.detail || t('orderSummaryError'));
    dispatch(createOrderFailed(errorData));
  }, [error, orderSummary, isLoading, dispatch, showErrorMessage, t]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" paddingBottom={20}>
        <Box flex={1} paddingRight={23}>
          <DetailsWithTitle title={t('details')}>
            {last_name && first_name && <OrderDetailsItem field={t('fullName')} value={`${first_name} ${last_name}`} />}

            {email && <OrderDetailsItem field={t('emailAddress')} value={email} />}

            {phone_number && <OrderDetailsItem field={t('mobileNumber')} value={phone_number} />}
          </DetailsWithTitle>
        </Box>
        <Box flex={1} paddingLeft={23}>
          {!orderSummary && isLoading ? (
            <ContentLoader height={'45vh'} />
          ) : (
            <DetailsWithTitle title={t('giftInformation')}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={7.5}
                className={classes.giftInformationItem}
              >
                {product && currency && (
                  <SingleGiftCard
                    cardImageSrc={product?.gift_card_image}
                    name={product?.product_name}
                    amount={amount}
                    currencyCode={currency?.currency_code}
                  />
                )}
              </Box>
              {orderSummary && <GiftOrderSummary summary={[orderSummary]} size="small" />}
            </DetailsWithTitle>
          )}
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" onClick={handleSubmit(onSubmit)}>
        <BaseButton type="submit" maxWidth={269}>
          {t('send')}
        </BaseButton>
      </Box>
    </>
  );
};
